import { useState, useEffect } from 'react';
import API from '../api/axios';
import { useToast } from '../context/ToastContext';
import './SMEStyles.css';

const CheckCircleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
const PointerIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11.24V7.5a2.5 2.5 0 015 0V12m0-2.5a2.5 2.5 0 015 0V15a6 6 0 01-6 6h-2.5a5 5 0 01-4-2l-3.5-4.5a1.5 1.5 0 012.5-1.5L9 14" />
  </svg>
);

const SMESubQuestionReview = () => {
  const { showToast } = useToast();

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ status: '', comments: '' });

  useEffect(() => {
    fetchPendingReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPendingReviews = async () => {
    try {
      setLoading(true);
      const { data } = await API.get('/sub-questions/sme/pending-reviews');
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      showToast('Failed to load pending reviews', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (!reviewForm.status) {
      showToast('Please select approve or reject', 'error');
      return;
    }

    try {
      await API.post(`/sub-questions/variations/${selectedReview.variation_id}/review`, reviewForm);
      showToast('Review submitted successfully!', 'success');
      setSelectedReview(null);
      setReviewForm({ status: '', comments: '' });
      await fetchPendingReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      showToast(error.response?.data?.message || 'Failed to submit review', 'error');
    }
  };

  const groupedReviews = reviews.reduce((acc, review) => {
    const key = review.full_question_number;
    if (!acc[key]) acc[key] = [];
    acc[key].push(review);
    return acc;
  }, {});

  return (
    <div className="sme-page">
      <div className="sme-page-header">
        <div>
          <h1>Sub-Question Variation Reviews</h1>
          <p>Review question variations sent by examiners</p>
        </div>
      </div>

      {loading ? (
        <div className="sme-spinner-wrap">
          <div className="sme-spinner" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="sme-card">
          <div className="sme-empty">
            <CheckCircleIcon />
            <h3>No pending reviews</h3>
            <p>New variations will appear here when examiners submit them.</p>
          </div>
        </div>
      ) : (
        <div className="sme-two-col">
          {/* Reviews List */}
          <div className="sme-card">
            <div className="sme-card-header">
              <h2>Pending Reviews</h2>
              <span className="sme-badge sme-badge--primary">{reviews.length}</span>
            </div>
            <div className="sme-card-body">
              {Object.entries(groupedReviews).map(([questionNumber, questionReviews]) => (
                <div key={questionNumber} className="sme-review-group">
                  <h4 className="sme-review-group-title">{questionNumber}</h4>
                  {questionReviews.map((review) => (
                    <button
                      key={review.review_id}
                      onClick={() => setSelectedReview(review)}
                      className={`sme-review-item ${selectedReview?.review_id === review.review_id ? 'sme-review-item--active' : ''}`}
                    >
                      <div className="sme-review-item-title">
                        {review.full_question_number}.{review.variation_number}
                      </div>
                      <div className="sme-review-item-meta">
                        {review.question_type} &bull; {review.marks} marks
                      </div>
                      <div className="sme-review-item-meta">From: {review.examiner_name}</div>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>

          {/* Review Panel */}
          <div>
            {selectedReview ? (
              <div className="sme-card">
                <div className="sme-card-header">
                  <h2>Review: {selectedReview.full_question_number}.{selectedReview.variation_number}</h2>
                </div>
                <div className="sme-card-body">
                  <div className="sme-detail-box">
                    <div className="sme-field">
                      <label className="sme-field-label">Question</label>
                      <p className="sme-field-value">{selectedReview.question_text}</p>
                    </div>

                    {selectedReview.options && (
                      <div className="sme-field">
                        <label className="sme-field-label">Options</label>
                        <div style={{ marginLeft: '0.5rem' }}>
                          {JSON.parse(selectedReview.options).map((opt, idx) => (
                            <div key={idx} className="sme-option-row">{opt}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="sme-field">
                      <label className="sme-field-label">Correct Answer</label>
                      <p className="sme-answer-box">{selectedReview.correct_answer}</p>
                    </div>

                    <div className="sme-detail-meta">
                      <span>Type: {selectedReview.question_type}</span>
                      <span>Marks: {selectedReview.marks}</span>
                      <span>Difficulty: {selectedReview.difficulty}</span>
                    </div>
                  </div>

                  <form onSubmit={handleReviewSubmit}>
                    <div className="sme-form-group">
                      <label className="sme-form-label">Decision *</label>
                      <div className="sme-choice-row">
                        <button
                          type="button"
                          onClick={() => setReviewForm({ ...reviewForm, status: 'approved' })}
                          className={`sme-choice ${reviewForm.status === 'approved' ? 'sme-choice--approve-active' : ''}`}
                        >
                          Approve
                        </button>
                        <button
                          type="button"
                          onClick={() => setReviewForm({ ...reviewForm, status: 'rejected' })}
                          className={`sme-choice ${reviewForm.status === 'rejected' ? 'sme-choice--reject-active' : ''}`}
                        >
                          Reject
                        </button>
                      </div>
                    </div>

                    <div className="sme-form-group">
                      <label className="sme-form-label">Comments</label>
                      <textarea
                        className="sme-form-textarea"
                        value={reviewForm.comments}
                        onChange={(e) => setReviewForm({ ...reviewForm, comments: e.target.value })}
                        rows="4"
                        placeholder="Add your feedback here..."
                      />
                    </div>

                    <button
                      type="submit"
                      className="sme-btn sme-btn--primary"
                      style={{ width: '100%' }}
                      disabled={!reviewForm.status}
                    >
                      Submit Review
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              <div className="sme-card">
                <div className="sme-empty">
                  <PointerIcon />
                  <h3>Select a variation</h3>
                  <p>Choose a variation from the list to review it.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SMESubQuestionReview;
